import type { PullRequestListResult } from "@cmmd-forge/contracts";
import {
  InfiniteQueryObserver,
  type InfiniteData,
  type QueryKey,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

type PageParam = { readonly limit: number; readonly cursors?: Record<string, string> };
type PullRequestInfiniteOptions<Key extends QueryKey> = UseInfiniteQueryOptions<
  PullRequestListResult,
  Error,
  InfiniteData<PullRequestListResult, PageParam>,
  Key,
  PageParam
>;
type PullRequestInfiniteResult = UseInfiniteQueryResult<
  InfiniteData<PullRequestListResult, PageParam>,
  Error
>;

/**
 * React Query has no `useInfiniteQueries` hook. This preserves one real infinite observer per
 * environment without conditionally calling hooks as connected environments appear or disappear.
 */
export function usePullRequestInfiniteQueries<Key extends QueryKey>(
  options: ReadonlyArray<PullRequestInfiniteOptions<Key>>,
): ReadonlyArray<PullRequestInfiniteResult> {
  const queryClient = useQueryClient();
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const queryKey = JSON.stringify(options.map((option) => option.queryKey));
  const environmentKey = JSON.stringify(options.map((option) => option.queryKey[1]));
  const [snapshot, setSnapshot] = useState<{
    readonly queryKey: string;
    readonly environmentKey: string;
    readonly results: ReadonlyArray<PullRequestInfiniteResult>;
  }>({ queryKey, environmentKey, results: [] });

  useEffect(() => {
    const observers = optionsRef.current.map(
      (option) => new InfiniteQueryObserver(queryClient, option),
    );
    const publish = () =>
      setSnapshot({
        queryKey,
        environmentKey,
        results: observers.map((observer) => observer.getCurrentResult()),
      });
    const unsubscribe = observers.map((observer) => observer.subscribe(publish));
    publish();
    return () => {
      unsubscribe.forEach((stop) => stop());
    };
  }, [environmentKey, queryClient, queryKey]);

  // Effects replace observers after render. A changed filter/search keeps the same positional
  // environment scope, so retain its last-good rows until React Query publishes the replacement.
  // A changed environment scope never borrows rows from another target.
  return snapshot.queryKey === queryKey || snapshot.environmentKey === environmentKey
    ? snapshot.results
    : [];
}
